import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users as UsersIcon,
  BarChart3,
  Plug,
  LogOut,
  Menu,    
  CreditCard,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../../components/ui/sheet";

import { useAuth } from "../../lib/auth";
import AppLogo from "../../components/AppLogo";

const NAV = [
  {
    to: "/admin",
    label: "Overview",
    icon: LayoutDashboard,
    end: true,
    testid: "admin-nav-overview",
  },
  {
    to: "/admin/hospitals",
    label: "Hospitals",
    icon: Building2,
    testid: "admin-nav-hospitals",
  },
  {
    to: "/admin/users",
    label: "Users",
    icon: UsersIcon,
    testid: "admin-nav-users",
  },
  {
    to: "/admin/reports",
    label: "Reports",
    icon: BarChart3,
    testid: "admin-nav-reports",
  },
  {
    to: "/admin/integrations",
    label: "Integrations",
    icon: Plug,
    testid: "admin-nav-integrations",
  },
  {
  to: "/admin/payment-providers",
  label: "Payment Providers",
  icon: CreditCard,
  testid: "admin-nav-payment-providers",
},
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const NavList = ({ onClick }) => (
    <nav className="space-y-1">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onClick}
          data-testid={item.testid}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
              isActive
                ? "border-teal-500/20 bg-teal-500/10 text-teal-300"
                : "border-transparent text-mb-fg2 hover:bg-mb-surface2 hover:text-white"
            }`
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  const Sidebar = ({ onClick }) => (
    <div className="flex h-full flex-col">
      <div className="border-b border-mb-border px-5 py-6">
        <AppLogo
          to="/admin"
          subtitle="Super Admin"
          size={40}
          onClick={onClick}
        />
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-5">
        <NavList onClick={onClick} />
      </div>

      <div className="border-t border-mb-border px-3 py-4">
        <div
          className="truncate px-3 pb-3 text-xs text-mb-fg2"
          data-testid="admin-sidebar-email"
        >
          {user?.email}
        </div>

        <Button
          onClick={onLogout}
          variant="outline"
          data-testid="admin-logout"
          className="w-full border-mb-border bg-transparent text-white hover:bg-mb-surface2"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-mb-bg text-white">
      <aside className="hidden w-72 flex-col border-r border-mb-border bg-mb-bg md:flex">
        <Sidebar />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center gap-3 border-b border-mb-border px-5 lg:px-8">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                data-testid="admin-mobile-menu"
                className="h-9 w-9 p-0 hover:bg-mb-surface2 md:hidden"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>

            <SheetContent
              side="left"
              className="w-72 border-mb-border bg-mb-bg p-0 text-white"
            >
              <Sidebar onClick={() => setOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="text-sm text-mb-fg2">
            <span>/</span>
            <span className="mx-1">super admin</span>
            <span>/</span>{" "}
            <span
              className="font-medium text-white"
              data-testid="admin-topbar-user"
            >
              {user?.full_name}
            </span>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden p-5 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}